import { App, Collapse } from "antd";
import type { User as AuthUser } from "firebase/auth";
import { ErrorAlert } from "@gull/ui";
import type { CreateMailOrderResponse, User } from "@gull/shared";
import CameraCapture from "../../components/CameraCapture";
import { STEPS } from "./constants";
import { buildMailOrderPayload } from "./payload";
import { isStepComplete, type StepCompletion } from "./types";
import { useIsMobile } from "./hooks/useIsMobile";
import { useContactDetails } from "./hooks/useContactDetails";
import { useDraftItems } from "./hooks/useDraftItems";
import { useOrderImages } from "./hooks/useOrderImages";
import { useStoneHandling } from "./hooks/useStoneHandling";
import { useWizardSteps } from "./hooks/useWizardSteps";
import { useSubmitMailOrder } from "./hooks/useSubmitMailOrder";
import { useDraftPersistence } from "./hooks/useDraftPersistence";
import ContactStep from "./ContactStep";
import ItemsStep from "./ItemsStep";
import StonesStep from "./StonesStep";
import SummaryStep from "./SummaryStep";
import StepLabel from "./StepLabel";
import StepProgress from "./StepProgress";
import FlowActions from "./FlowActions";
import HelpCard from "./HelpCard";

export interface BestillPoseWizardProps {
  /** Signed in: the host gates on authentication and identity before rendering the wizard. */
  user: AuthUser;
  /** The host's `useProfile(user.uid)` — `undefined` until the first snapshot arrives. */
  profile: User | null | undefined;
  /** The host decides what follows a completed order: navigate away, or confirm in place. */
  onSubmitted: (result: CreateMailOrderResponse) => void;
}

/**
 * The order wizard itself, without any page chrome: the standalone page and the frame TRY embeds
 * both render it, and each brings its own heading, navigation and post-submit behaviour.
 */
export default function BestillPoseWizard({ user, profile, onSubmitted }: BestillPoseWizardProps) {
  const { message } = App.useApp();
  const isMobile = useIsMobile();

  const contact = useContactDetails(profile, user.email ?? null);
  const draftItems = useDraftItems();
  const images = useOrderImages({ user, onItemsSuggested: draftItems.mergeAnalyzedItems });
  const stones = useStoneHandling(draftItems.items.length);
  const submitter = useSubmitMailOrder();

  const completion: StepCompletion = {
    contact: contact.isComplete,
    items: draftItems.allDescribed && !images.busy,
    stones: !stones.selectionMissing,
    terms: stones.salesTermsAccepted,
  };

  const canLeaveStep = async (step: number) => {
    if (step === 0) {
      if (!completion.contact) {
        contact.markTouched();
        message.error(
          contact.emailValid
            ? "Fyll inn adresse og gyldig kontoinformasjon før du går videre."
            : "Fyll inn en gyldig e-postadresse før du går videre.",
        );
        return false;
      }
      return contact.persist();
    }
    if (step === 1 && !completion.items) {
      message.error(
        "Legg inn minst én beskrivelse og vent til eventuelle bilder er ferdig lagret/analysert.",
      );
      return false;
    }
    if (step === 2 && !completion.stones) {
      message.error("Velg minst én gjenstand som skal få fjernet stener, perler eller ur.");
      return false;
    }
    return true;
  };

  const steps = useWizardSteps({ stepCount: STEPS.length, canLeaveStep });

  const draft = useDraftPersistence({
    user,
    draftItems,
    images,
    stones,
    activeStep: steps.activeStep,
    stepInUrl: steps.stepInUrl,
    goToStep: steps.goTo,
  });

  const canSubmit =
    !submitter.submitting &&
    completion.contact &&
    completion.items &&
    completion.stones &&
    completion.terms;

  const submit = async () => {
    for (const step of [0, 1, 2]) {
      if (!(await canLeaveStep(step))) return;
    }
    if (!completion.terms) {
      message.error("Du må godta vilkårene ved salg før du fullfører.");
      return;
    }
    const result = await submitter.submit(
      buildMailOrderPayload({
        address: contact.address,
        stoneMode: stones.mode,
        selectedStoneItemIndexes: stones.selectedItemIndexes,
        items: draftItems.items,
        orderImagePaths: images.paths,
        bankAccountNumber: contact.bankAccountNumber,
        kontoEier: contact.kontoEier,
        salesTermsAccepted: stones.salesTermsAccepted,
      }),
    );
    if (result) {
      draft.clear();
      onSubmitted(result);
    }
  };

  const name = profile?.name ?? user.displayName ?? "";
  const phone = profile?.phone ?? user.phoneNumber ?? "";
  const error = submitter.error ?? contact.persistError;

  const sections = [
    <ContactStep key="contact" name={name} phone={phone} contact={contact} />,
    <ItemsStep key="items" items={draftItems} images={images} />,
    <StonesStep key="stones" items={draftItems.items} stones={stones} />,
    <SummaryStep
      key="summary"
      name={name}
      email={contact.email}
      phone={phone}
      address={contact.address}
      bankAccountNumber={contact.bankAccountNumber}
      kontoEier={contact.kontoEier}
      items={draftItems.items}
      orderImagePaths={images.paths}
      stoneMode={stones.mode}
      selectedStoneItemIndexes={stones.selectedItemIndexes}
      salesTermsAccepted={stones.salesTermsAccepted}
      onSalesTermsChange={stones.setSalesTermsAccepted}
    />,
  ];

  const flowActions = (
    <FlowActions
      activeStep={steps.activeStep}
      canSubmit={canSubmit}
      submitting={submitter.submitting}
      onBack={steps.goBack}
      onNext={() => void steps.goNext()}
      onSubmit={() => void submit()}
    />
  );

  return (
    <>
      {isMobile ? (
        <Collapse
          className="sendin-mobile-steps"
          activeKey={[String(steps.activeStep)]}
          onChange={(key) => {
            const next = Number(Array.isArray(key) ? key[0] : key);
            if (Number.isFinite(next)) steps.goTo(next);
          }}
          items={STEPS.map((step, index) => ({
            key: String(index),
            label: (
              <StepLabel
                index={index}
                label={step.label}
                complete={isStepComplete(index, completion, steps.activeStep)}
              />
            ),
            children: sections[index],
          }))}
        />
      ) : (
        <>
          <StepProgress
            activeStep={steps.activeStep}
            completion={completion}
            onSelect={steps.goTo}
          />
          <div className="sendin-layout">
            <div>
              {sections[steps.activeStep]}
              {flowActions}
              <ErrorAlert error={error} />
            </div>
            <HelpCard />
          </div>
        </>
      )}

      {isMobile ? (
        <>
          <HelpCard mobile />
          {flowActions}
          <ErrorAlert error={error} />
        </>
      ) : null}

      <CameraCapture
        open={images.cameraOpen}
        onCancel={images.closeCamera}
        onCapture={(file) => {
          images.closeCamera();
          void images.uploadCapturedFile(file);
        }}
      />
    </>
  );
}
